import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

// component
import TopicCard from '../components/TopicCard';

// mui
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// types
type TopicListPropsType = {
    isShowCardActions: boolean,
    topicList: TopicListType[],
    setIsEdit: React.Dispatch<React.SetStateAction<boolean>>,
    setIsOpenConfirmDialog?: React.Dispatch<React.SetStateAction<boolean>>,
    setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>,
    setTargetEditTopic: React.Dispatch<React.SetStateAction<number>>,
    setTargetDeleteTopic?: React.Dispatch<React.SetStateAction<number>>,
    setTopicType?: React.Dispatch<React.SetStateAction<"draft" | "published" | "new">>,
}

type TopicListType = {
    body: string,
    commentCount: number,
    openRange: number, // 公開範囲(招待:0 / 公開:1)
    postStatus: number, // 投稿ステータス(下書き:0 / 投稿:1)
    postTime: string, // 投稿時間 (yyyy-MM-dd HH:mm:ss)
    title: string,
    topicId: number,
    topicMembers: null | {
        email: string,
        userId: number,
        userName: string,
    }[],
    updatedTime: string, // 更新時間 (yyyy-MM-dd HH:mm:ss)
    userId: number,
    userName: string,
}

export default function TopicList(props: TopicListPropsType) {
    const {
        isShowCardActions,
        topicList,
        setIsEdit,
        setIsOpenConfirmDialog,
        setIsOpenModal,
        setTargetEditTopic,
        setTargetDeleteTopic,
        setTopicType,
    } = props;

    // ソート
    const [sort, setSort] = useState('');

    const fnChangeSort = useCallback((topicList: TopicListType[], sort: string) => {
        if (sort === "date-desc") {
            sortDateDesc(topicList);
        } else if (sort === "date-asc") {
            sortDateAsc(topicList);
        } else {
            sortDateDesc(topicList);
        }
    }, []);

    const handleChangeSort = (event: SelectChangeEvent, topicList: TopicListType[]) => {
        fnChangeSort(topicList, event.target.value);
        setSort(event.target.value);
    }

    // 更新日（新しい順）
    const sortDateDesc = (topicList: TopicListType[]) => {
        const tempTopicList = [...topicList].sort((a, b) => {
            const aDateMillis = new Date(a.updatedTime).getTime();
            const bDateMillis = new Date(b.updatedTime).getTime();
            return -(aDateMillis - bDateMillis);

        });
        setInternalTopicList(tempTopicList);
    }

    // 更新日（古い順）
    const sortDateAsc = (topicList: TopicListType[]) => {
        const tempTopicList = [...topicList].sort((a, b) => {
            const aDateMillis = new Date(a.updatedTime).getTime();
            const bDateMillis = new Date(b.updatedTime).getTime();
            return aDateMillis - bDateMillis;
        });
        setInternalTopicList(tempTopicList);
    }

    /**
     * トピック一覧
     */
    const [internalTopicList, setInternalTopicList] = useState<TopicListType[]>([]);

    useEffect(() => {
        fnChangeSort(topicList, sort);
    }, [fnChangeSort, sort, topicList]);

    // ページネーション
    const [page, setPage] = useState<number>(1); // ページ番号
    const [pageCount, setPageCount] = useState<number>(1); // ページ数
    const [displayedTopicItems, setDisplayedTopicItems] = useState<TopicListType[]>([]); // 表示データ
    const displayNum = 10; //1ページあたりの項目数

    useEffect(() => {
        if (internalTopicList) {
            //ページカウントの計算（今回は3項目/ページなので4ページ）
            setPageCount(Math.ceil(internalTopicList.length / displayNum));
            //表示データを抽出
            setDisplayedTopicItems(internalTopicList.slice(((page - 1) * displayNum), page * displayNum))
        }
    }, [internalTopicList, page]);

    const handleChangePagination = (event: React.ChangeEvent<unknown>, page: number) => {
        //ページ移動時にページ番号を更新
        setPage(page);
        //ページ移動時に表示データを書き換える
        setDisplayedTopicItems(internalTopicList.slice(((page - 1) * displayNum), page * displayNum))
    }

    return (
        <>
            <Box
                sx={{
                    maxWidth: 980,
                    mx: "auto",
                    pt: 3,
                    px: 3,
                }}
            >
                <Stack
                    alignItems="center"
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{
                        flexWrap: { mb: "wrap", tb: "nowrap" }
                    }}
                >
                    <Box
                        order={1}
                    >
                        <Typography variant='body2'>
                            トピック数：{internalTopicList.length.toString()}
                        </Typography>
                    </Box>
                    <Box
                        order={{ mb: 3, tb: 2 }}
                        sx={{
                            width: { mb: "100%", tb: "auto" }
                        }}
                    >
                        <Pagination
                            count={pageCount}
                            page={page}
                            onChange={handleChangePagination}
                            sx={{
                                '& .MuiPagination-ul': {
                                    justifyContent: "center"
                                }
                            }}
                        />
                    </Box>
                    <Box
                        order={{ mb: 2, tb: 3 }}
                    >
                        <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                            <InputLabel id="select-helper-label">並び替え</InputLabel>
                            <Select
                                labelId="select-helper-label"
                                id="select-helper"
                                value={sort}
                                label="並び替え"
                                onChange={(event) => handleChangeSort(event, internalTopicList)}
                            >
                                <MenuItem value="">
                                    <em>未選択</em>
                                </MenuItem>
                                <MenuItem value={"date-desc"}>更新日（新しい順）</MenuItem>
                                <MenuItem value={"date-asc"}>更新日（古い順）</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Stack>
            </Box>
            {/* トピック一覧 */ }
            {internalTopicList &&
                <List>
                    {displayedTopicItems.map((item) => (
                        <ListItem key={item.topicId}>
                            <TopicCard
                                body={item.body}
                                commentCount={item.commentCount}
                                isShowCardActions={isShowCardActions}
                                postStatus={item.postStatus}
                                postTime={item.postTime}
                                setIsEdit={setIsEdit}
                                setIsOpenConfirmDialog={setIsOpenConfirmDialog}
                                setIsOpenModal={setIsOpenModal}
                                setTargetEditTopic={setTargetEditTopic}
                                setTargetDeleteTopic={setTargetDeleteTopic}
                                setTopicType={setTopicType}
                                title={item.title}
                                topicId={item.topicId}
                                topicMembers={item.topicMembers}
                                updatedTime={item.updatedTime}
                                userId={item.userId}
                                userName={item.userName}
                            />
                            {/*setTargetEditCommentContent={setTargetEditCommentContent}*/}
                        </ListItem>
                    ))}
                </List>
            }
            {/* ページネーション */ }
            <Box
                sx={{
                    maxWidth: 980,
                    mx: "auto",
                    pt: 2,
                    pb: 10,
                }}
            >
                <Pagination
                    count={pageCount}
                    page={page}
                    onChange={handleChangePagination}
                    sx={{
                        mx: "auto",
                        '& .MuiPagination-ul': {
                            justifyContent: "center"
                        }
                    }}
                />
            </Box>
        </>
    )
}